import store from "@/store";
import { err, ok, Result } from "neverthrow";
import { ComponentMutation } from "@/framework/store/component/types";
import { NotifyMyEvent } from "@/framework/services/events/subscription";
import { GeneralResponse } from "@/framework/objects/GeneralResponse";
import {
  isPing,
  logToDataDogIfWsErrorMessage,
  parseWsSystemMessage,
  PONG,
  WEBSOCKET_KEEP_ALIVE,
  WsMessage,
} from "./websocket";
import { EventEmitter, IEventEmitter } from "@/framework/eventEmitter/eventEmitter";
import { KtznProject, ProjectService } from "@/framework/services/general/projectService";
import { AuthService } from "@/framework/api/AuthService";
import KtznWebsocketError from "@/framework/errors/websocket/ktznWebsocketError";
import { isEmpty } from "@/framework/utilities/isEmpty";

/**
 * SendEventType ('subscribe' or 'unsubscribe')
 */
export enum SendEventType {
  Subscribe = "subscribe",
  Unsubscribe = "unsubscribe",
}

/**
 * Represents a ktzn subscribe websocket event that is expected by the backend.
 */
export type KtznSubscribeEvent = {
  /**
   * Event string
   *
   * - subscribe
   * - unsubscribe
   */
  event: string;
  /**
   * Subscription name. Available subscription types are `user` or `conversation`.
   *
   * - user/[accessId]
   * - conversation/[conversationId]
   */
  subscribe_name: string;
  /**
   * Base64 encoded cognito token string
   */
  token: string;
  /**
   * User agent string from the Navigator (Browser).
   *
   * A DOMString specifying the complete user agent string the browser provides both in HTTP headers and in response to this and other related methods on the Navigator object.
   * Source: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/userAgent
   */
  user_agent: string;
  /**
   * Authentication method.
   *
   * - `undefined` for Cognito
   * - `ktzn_auth` for Kotozna Auth
   */
  method: string | undefined;
};

export interface IKtznWebsocketService {
  /**
   * Returns if the websocket is currently connected.
   */
  isConnected: boolean;
  /**
   * Websocket 'connected' event publisher.
   */
  connected: IEventEmitter<any>;
  /**
   * Websocket 'system message' event publisher.
   *
   * System events from the `ktzn-chat-websocket-server` about the connection or subscription status.
   * E.g. connected, subscribe, unsubscribe or error.
   */
  systemMessage: IEventEmitter<WsMessage>;
  /**
   * Websocket `Kotozna` message event publisher.
   *
   * `Kotozna` messages or updates sent from `ktzn-be`.
   * E.g. `New messages`, `message updates`, `conversation status updates`, `tenant events` or `user events`.
   */
  message: IEventEmitter<NotifyMyEvent>;
  /**
   * Websocket `failure` (`error`) event publisher.
   */
  failure: IEventEmitter<NotifyMyEvent>;
  /**
   * Websocket `disconnect` event publisher.
   */
  disconnected: IEventEmitter<any>;
  updateWebSocketURI: (uri: string) => string;

  /**
   * Connects to the websocket server.
   */
  connect(uri?: string): boolean;

  /**
   * Update the auto-reconnect property.
   * @param newValue If the websocket should auto-reconnect on disconnect.
   * @returns set value
   */
  setAutoReconnect(newValue: boolean): boolean;

  /**
   * Re-connects to the websocket server. Requires a minimum value of `1000ms`.
   * @param delay (Optional) Timeout before reconnecting (Default: `1000ms`)
   */
  reconnect(delay?: number): NodeJS.Timeout;

  /**
   * Responds to the keep alive heartbeat from the server.
   * @param keepAlive Amount of milliseconds to wait until response
   * @param pong Value to send back to websocket server
   */
  handleHeartbeat(keepAlive: number, pong: Uint8Array): void;

  /**
   * Sends a websocket subscribe event to the server.
   * @param event SendEventType (`subscribe` or `unsubscribe`)
   * @param subscribeName Subscription name (e.g. 'conversations/<id>')
   * @param token Cognito access token string
   * @param method (Optional) Authentication method (Default: `undefined` for Cognito. Use `be_auth` string for Kotozna Auth)
   * @returns Result with either sent KtznSubscribeEvent or an KtznWebsocketError
   */
  send(
    event: SendEventType,
    subscribeName: string,
    token: string,
    method?: string
  ): Result<KtznSubscribeEvent, KtznWebsocketError>;

  /**
   * Sends a websocket subscribe event to the server.
   * @param event KtznSubscribeEvent
   * @returns Result with either sent KtznSubscribeEvent or an KtznWebsocketError
   */
  sendEvent(event: KtznSubscribeEvent): Result<KtznSubscribeEvent, KtznWebsocketError>;

  /**
   * Tells the ktzn-be that the event was successfully received.
   * If this event is not send, the `ktzn-be` will retry sending 5 times.
   *
   * Important: This method needs to be called every time when a event is received!
   * @param eventId The event id from the received event
   * @returns Result with either an GeneralResponse or an KtznWebsocketError
   */
  sendEventReceived(eventId: number): Promise<Result<GeneralResponse, KtznWebsocketError>>;
}

/**
 * Wrapper around the Javascript WebSocket API.
 *
 * Handles the websocket connection to the
 * - Kotozna websocket server (`https://bitbucket.org/kotozna/ktzn-chat-websocket-server/`) and
 * - Kotozna flask backend (`https://bitbucket.org/kotozna/ktzn-chat-be/`).
 */
export class KtznWebsocketService implements IKtznWebsocketService {
  // MARK: - Properties
  private static _instance: IKtznWebsocketService;

  /**
   * URL to connect to the Kotozna websocket server.
   */
  private _websocketURI: string;
  /**
   * Websocket connection instance.
   */
  private _websocket: WebSocket | undefined;
  /**
   * Flag that indicates if the websocket should auto-reconnect once its disconnected.
   */
  private _autoReconnect: boolean;
  /**
   * Access id of the current user.
   * Needed for sending the access log.
   */
  private userAccessId: string | undefined;
  /**
   *
   */
  private _delay: number | undefined;
  /**
   * Websocket connected event publisher(Event emitter).
   */
  private readonly onConnected = new EventEmitter<any>();
  /**
   * Incoming websocket message event publisher(Event emitter).
   */
  private readonly onSystemMessageEvent = new EventEmitter<WsMessage>();
  /**
   * Incoming websocket message event publisher(Event emitter).
   */
  private readonly onMessageEvent = new EventEmitter<NotifyMyEvent>();
  /**
   * Websocket failure event publisher(Event emitter).
   */
  private readonly onFailure = new EventEmitter<any>();
  /**
   * Websocket disconnected publisher(Event emitter).
   */
  private readonly onDisconnected = new EventEmitter<any>();

  private constructor(websocketURI: string, autoReconnect: boolean) {
    this._websocketURI = websocketURI;
    this._autoReconnect = autoReconnect;
  }

  /**
   * Flag that indicates if the websocket is currently connected.
   */
  private _isConnected = false;

  /**
   * Returns if the websocket is currently connected.
   */
  public get isConnected(): boolean {
    return this._isConnected;
  }

  // MARK: - Event publishers

  /**
   * Websocket `connected` event publisher.
   */
  public get connected(): IEventEmitter<any> {
    return this.onConnected.expose();
  }

  /**
   * Websocket 'system message' event publisher.
   *
   * System events from the `ktzn-chat-websocket-server` about the connection or subscription status.
   * E.g. connected, subscribe, unsubscribe or error.
   */
  public get systemMessage(): IEventEmitter<WsMessage> {
    return this.onSystemMessageEvent.expose();
  }

  /**
   * `Kotozna` message event publisher.
   *
   * `Kotozna` messages or updates sent from `ktzn-be`.
   * E.g. `New messages`, `message updates`, `conversation status updates`, `tenant events` or `user events`.
   */
  public get message(): IEventEmitter<NotifyMyEvent> {
    return this.onMessageEvent.expose();
  }

  /**
   * Websocket `failure` (`error`) event publisher.
   */
  public get failure() {
    return this.onFailure.expose();
  }

  /**
   * Websocket `disconnect` event publisher.
   */
  public get disconnected() {
    return this.onDisconnected.expose();
  }

  public static shared(
    websocketURI: string = `${process.env.VUE_APP_WEBSOKCET_URI}/conversation`,
    autoReconnect: boolean = true
  ) {
    return this._instance || (this._instance = new this(websocketURI, autoReconnect));
  }

  /**
   * Update the auto-reconnect property.
   * @param newValue If the websocket should auto-reconnect on disconnect.
   * @returns set value
   */
  setAutoReconnect = (newValue: boolean): boolean => {
    this._autoReconnect = newValue;
    return this._autoReconnect;
  };

  public updateWebSocketURI = (uri: string): string => {
    if (!uri || uri == "") throw new Error("KtznWebsocketService - error: Invalid WebSocket URI - empty");
    let updateUri = uri;
    if (!updateUri.startsWith("wss://")) updateUri = "wss://" + updateUri;
    if (!updateUri.includes("/conversation")) updateUri = updateUri + "/conversation";
    return updateUri;
  };
  // MARK: - Websocket lifecycle
  /**
   * Connects to the websocket server.
   */
  connect = (uri: string = this._websocketURI): boolean => {
    try {
      this._websocketURI = this.updateWebSocketURI(uri);
    } catch (error) {
      console.error("KtznWebsocketService - error: Invalid WebSocket URI", error);
      return false;
    }

    if (this._isConnected) {
      console.log("KtznWebsocketService - connect(): Websocket already connected");
      return true;
    }
    try {
      console.log("KtznWebsocketService - connect(): Connecting websocket...");
      this._websocket = new WebSocket(this._websocketURI);
      this._websocket.onopen = this.onOpen;
      this._websocket.onmessage = this.onMessage;
      this._websocket.onerror = this.onError;
      this._websocket.onclose = this.onClose;
      return true;
    } catch (error) {
      console.log("KtznWebsocketService - error: ", error);
      return false;
    }
  };

  /**
   * Re-connects to the websocket server. Requires a minimum value of `1000ms`.
   * @param delay (Optional) Timeout before reconnecting (Default: `1000ms`)
   */
  reconnect = (delay = 1000): NodeJS.Timeout => {
    console.log("### should reconnect websocket after ", delay, "ms");
    this._delay = delay;
    return setTimeout(() => {
      if (this._autoReconnect) {
        console.log("Reconnecting to websocket...");
        this.connect();
      } else {
        console.log("Not reconnecting because `autoReconnect` has been set to false.");
      }
    }, delay);
  };

  /**
   * Responds to the keep alive heartbeat from the server.
   * @param keepAlive Amount of milliseconds to wait until response
   * @param pong Value to send back to websocket server
   */
  handleHeartbeat = (keepAlive: number, pong: Uint8Array): void => {
    console.log("'Ping' received from WebSocket server");
    const self = this;
    setTimeout(() => {
      if (self._isConnected && self._websocket) {
        console.log("Sending 'Pong' to WebSocket server");
        self._websocket.send(pong);
      }
    }, keepAlive);
  };

  /**
   * Sends a websocket subscribe event to the server.
   * @param event SendEventType (`subscribe` or `unsubscribe`)
   * @param subscribeName Subscription name (e.g. 'conversations/<id>')
   * @param token Cognito access token string
   * @param method (Optional) Authentication method (Default: `undefined` for Cognito. Use `be_auth` string for Kotozna Auth)
   * @returns KtznSubscribeEvent
   */
  send = (
    event: SendEventType,
    subscribeName: string,
    token: string,
    method?: string
  ): Result<KtznSubscribeEvent, KtznWebsocketError> => {
    if (!token) {
      return err(new KtznWebsocketError(KtznWebsocketError.Type.noToken));
    }
    const sendEvent = {
      event: event.toString(),
      subscribe_name: subscribeName,
      token: token,
      user_agent: navigator.userAgent,
      method: method,
    } as KtznSubscribeEvent;
    const res = this.sendEvent(sendEvent);
    if (res.isOk()) {
      return ok(res.value);
    } else {
      return err(res.error);
    }
  };

  /**
   * Sends a websocket subscribe event to the server.
   * @param event KtznSubscribeEvent
   * @returns Result with either sent KtznSubscribeEvent or an KtznWebsocketError
   */
  sendEvent = (event: KtznSubscribeEvent): Result<KtznSubscribeEvent, KtznWebsocketError> => {
    if (!event) return err(new KtznWebsocketError(KtznWebsocketError.Type.noEvent));
    if (!this._websocket || !(this._websocket.readyState === WebSocket.OPEN) || !this.isConnected) {
      return err(new KtznWebsocketError(KtznWebsocketError.Type.noWebsocketConnection));
    }
    const data = JSON.stringify(event);
    this._websocket.send(data);
    return ok(event);
  };

  /**
   * Tells the ktzn-be that the event was successfully received.
   * If this event is not send, the `ktzn-be` will retry sending 5 times.
   *
   * Important: This method needs to be called every time when a event is received!
   * @param eventId The event id from the received event
   * @returns Result with either an GeneralResponse or an KtznWebsocketError
   */
  sendEventReceived = async (eventId: number): Promise<Result<GeneralResponse, KtznWebsocketError>> => {
    if (!eventId || eventId == 0) err(new KtznWebsocketError(KtznWebsocketError.Type.noEvent));
    try {
      const resp = await new AuthService().receiveEvent({ eventId: eventId });
      if (resp.isOk()) {
        const value = resp.value as GeneralResponse;
        return ok(value);
      }
      return err(new KtznWebsocketError(KtznWebsocketError.Type.noGraphqlObject));
    } catch (error) {
      return err(new KtznWebsocketError(KtznWebsocketError.Type.network, error));
    }
  };

  // MARK: - Websocket connection

  /**
   * Websocket `onopen` handler
   * @param event Websocket event
   */
  private onOpen = (event: any): void => {
    this._isConnected = true;
    this.onConnected.trigger(event);
  };

  /**
   * Websocket `onmessage` handler
   * @param event Websocket event
   */
  private onMessage = async (event: any): Promise<void> => {
    // Heartbeat (= Ping / Pong with WebSocket server)
    const data = event.data;
    if (await isPing(data)) {
      this.handleHeartbeat(WEBSOCKET_KEEP_ALIVE, PONG);
      return;
    }

    // Check if system message
    try {
      const wsSystemMessage = parseWsSystemMessage(data);
      logToDataDogIfWsErrorMessage(wsSystemMessage, this.userAccessId);
      // Todo: Use 'wsSystemMessage' to react to ws server events
      // console.log("wsSystemMessage: ", wsSystemMessage);
      if (wsSystemMessage) {
        this.onSystemMessageEvent.trigger(wsSystemMessage);
      }
    } catch (error) {
      console.log("wsSystemMessage error: ", error);
    }

    // websocketでメッセージを受信した時
    // 戻り値チェック
    if (event && event.data) {
      let parsedData = JSON.parse(event.data);
      if (parsedData.body) {
        try {
          parsedData = JSON.parse(parsedData.body);
          // console.log("***** parsedData=", parsedData);

          let receivedEventIdList = [];

          if (!isEmpty(store.getters.recievedEventIdList)) receivedEventIdList = store.getters.recievedEventIdList;

          // console.log("***** receivedEventIdList=", receivedEventIdList);
          if (
            parsedData.event_id &&
            receivedEventIdList.length > 0 &&
            receivedEventIdList.indexOf(parsedData.event_id) >= 0
          ) {
            console.log("this event is already received", parsedData.event_id);
            return;
          }
          let event = parsedData as NotifyMyEvent;
          // Emit event
          this.onMessageEvent.trigger(event); // Emit event
          this.sendEventReceived(event.event_id); // Notify ktzn-be about received
          // Add event id to already received eventId list (receivedEventIdList)
          store.commit(ComponentMutation.ADD_EVENT_ID, parsedData.event_id);
        } catch (exception) {
          console.log(exception);
        }
      }
    }
  };

  /**
   * Websocket `onerror` handler
   * @param event Websocket event
   */
  private onError = (event: any): void => {
    // websocketでエラーが発生した時
    console.log("### websocket.onerror()", event);
    this.onFailure.trigger(event); // Emit event
  };

  /**
   * Websocket `onclose` handler
   * @param event Websocket event
   */
  private onClose = (event: any): void => {
    console.log("### websocket.onclose()", event);
    this._isConnected = false;
    this.onDisconnected.trigger(event); // Emit event
    // Only reconnect if auto-reconnect is set to true
    const delay: number | undefined = this._delay ? this._delay : undefined;
    const project = new ProjectService().getCurrent();
    if (this._autoReconnect && project != KtznProject.Agent) this.reconnect(delay);
  };
}

export default KtznWebsocketService.shared();
